import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Skeleton } from '@mui/material';
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store/index';
import dayjs, { Dayjs } from 'dayjs';
import RangeDatePicker from 'components/common/RangeDatePicker/RangeDatePicker';
import { GridColDef } from '@mui/x-data-grid';
import StatDataGridDetail from './StatDataGridDetail';
import StatCard from './StatCard';
import SystemsOverview from './SystemsOverview';
import { statisticsService, SiteStatisticsData, BonusStatistic } from 'components/api/statistics';
import { transactionService } from 'components/api/transactions';
import { userService } from 'components/api/users';
import { promoCodeService } from 'components/api/promoCodes';
import styles from './Statistics.module.scss';

import {
  BarChart3,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Hash,
  RotateCcw,
  Target,
  Calendar,
  Gift,
  Zap,
  PieChart,
  Coins,
  Undo,
  Crown,
} from 'lucide-react';
import SiteSelector from 'components/common/SiteSelector/SiteSelector';
const Statistics: React.FC = () => {
  const navigate = useNavigate();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );
  const theme = useAppSelector((state: RootState) => state.ui.theme);

  const [selectedSite, setSelectedSite] = useState<string | number>('all');
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().subtract(1, 'week'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());

  // API State
  const [stats, setStats] = useState<SiteStatisticsData | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const [activeView, setActiveView] = useState<'dashboard' | 'detail'>('dashboard');
  const [modalTitle, setModalTitle] = useState('');
  const [modalColumns, setModalColumns] = useState<GridColDef[]>([]);
  const [modalFetchFn, setModalFetchFn] = useState<
    ((page: number, limit: number) => Promise<{ rows: any[]; total: number }>) | undefined
  >();

  useEffect(() => {
    if (sites.length > 0 && selectedSite === 'all') {
      const dgentaSite = sites.find((s) => s.title.toLowerCase().includes('dgenta'));
      if (dgentaSite) {
        setSelectedSite(dgentaSite.id);
      }
    }
  }, [sites, selectedSite]);

  const fromStr = fromDate?.toISOString();
  const toStr = toDate?.toISOString();

  const handleShowStatistics = React.useCallback(async () => {
    if (!fromStr || !toStr || selectedSite === 'all') return;

    try {
      setIsLoadingStats(true);
      const data = await statisticsService.getSiteStatistics({
        siteId: selectedSite,
        from: fromStr,
        to: toStr,
      });
      setStats(data.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [fromStr, toStr, selectedSite]);

  useEffect(() => {
    handleShowStatistics();
  }, [handleShowStatistics]);

  const getBonusStats = (type: string): BonusStatistic => {
    return (
      stats?.bonuses?.find((b) => b.type === type) || {
        type,
        count: 0,
        activated: 0,
        notActivated: 0,
        amount: '0',
      }
    );
  };

  const renderUsernameCell = (params: any) => (
    <span
      style={{ cursor: 'pointer', color: '#1fa2ff', textDecoration: 'underline' }}
      onClick={() => {
        if (params.row.userId) {
          setActiveView('dashboard');
          navigate(`/clients/${params.row.userId}`);
        }
      }}
    >
      {params.value}
    </span>
  );

  const renderAmountCell = (params: any) => {
    const val = params.value;
    if (val === '-' || val == null) return '-';
    const num = Number(val);
    if (!isNaN(num)) {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${val}`;
  };

  const renderDateCell = (params: any) => {
    const val = params.value;
    if (!val || val === '-') return '-';
    // Use dayjs directly. If val is a string from the API (ISO format), dayjs will parse it properly.
    return dayjs(val).format('DD/MM/YYYY HH:mm');
  };

  const defaultUserColumns: GridColDef[] = [
    { field: 'username', headerName: 'User', flex: 1, renderCell: renderUsernameCell },
    { field: 'email', headerName: 'Email', flex: 1 },
  ];

  const newUsersColumns: GridColDef[] = [
    ...defaultUserColumns,
    {
      field: 'isEmailVerified',
      headerName: 'Verified',
      flex: 1,
      renderCell: (params: any) => (
        <span className={!params.value ? styles.negativeAmountCell : undefined}>
          {params.value ? 'Yes' : 'No'}
        </span>
      ),
    },
  ];

  const firstDepositUserColumns: GridColDef[] = [
    ...defaultUserColumns,
    {
      field: 'firstDepositAt',
      headerName: 'First Deposit Date',
      flex: 1,

      renderCell: renderDateCell,
    },
  ];

  const bonusUserColumns: GridColDef[] = [
    { field: 'username', headerName: 'User', flex: 1, renderCell: renderUsernameCell },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      cellClassName: () => styles.amountCell,
      renderCell: renderAmountCell,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: renderDateCell,
    },
  ];

  const withdrawUserColumns: GridColDef[] = [
    { field: 'username', headerName: 'User', flex: 1, renderCell: renderUsernameCell },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      cellClassName: () => styles.amountCell,
      renderCell: renderAmountCell,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params: any) => {
        const status = params.value;
        const color =
          status === 'completed'
            ? '#2ecc71'
            : status === 'pending' || status === 'processing'
              ? '#f39c12'
              : status === 'failed' || status === 'cancelled'
                ? '#e74c3c'
                : 'inherit';
        return (
          <span style={{ color, textTransform: 'capitalize', fontWeight: 500 }}>{status}</span>
        );
      },
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: renderDateCell,
    },
  ];

  const promoUserColumns: GridColDef[] = [
    { field: 'code', headerName: 'Promo Code', flex: 1 },
    { field: 'username', headerName: 'User', flex: 1, renderCell: renderUsernameCell },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      cellClassName: () => styles.amountCell,
      renderCell: renderAmountCell,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: renderDateCell,
    },
  ];

  const wheelUserColumns: GridColDef[] = [
    { field: 'username', headerName: 'User', flex: 1, renderCell: renderUsernameCell },
    {
      field: 'levelKey',
      headerName: 'Level',
      flex: 1,
    },
    {
      field: 'prizeAmount',
      headerName: 'Amount',
      flex: 1,
      cellClassName: () => styles.amountCell,
      renderCell: renderAmountCell,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      renderCell: renderDateCell,
    },
  ];

  const fetchAndOpenList = (
    title: string,
    fetchFn: (page: number, limit: number) => Promise<any>,
    columns: GridColDef[],
    formatData: (items: any[]) => any[] = (items) => items,
  ) => {
    setModalTitle(title);
    setModalColumns(columns);
    setModalFetchFn(() => async (page: number, limit: number) => {
      const res = await fetchFn(page, limit);
      const items = res?.data?.docs || res?.data?.items || res?.items || res?.data || [];
      const total =
        res?.totalDocs || res?.total || res?.data?.totalDocs || res?.data?.total || items.length;
      return { rows: formatData(items), total };
    });
    setActiveView('detail');
  };

  const renderSkeletonCard = (key: number) => (
    <Box key={key} className={styles.statCard} sx={{ bgcolor: 'var(--bg-panel)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        />
        <Box>
          <Skeleton
            variant="text"
            width={80}
            height={16}
            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', mb: 0.5 }}
          />
          <Skeleton
            variant="text"
            width={120}
            height={24}
            sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
          />
        </Box>
      </Box>
      <Skeleton
        variant="rectangular"
        height={60}
        sx={{ borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.05)', mt: 1 }}
      />
      <Skeleton
        variant="rectangular"
        height={32}
        sx={{ borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.05)', mt: 2 }}
      />
    </Box>
  );

  const renderSkeletons = () => (
    <Box className={styles.resultsWrapper}>
      <Skeleton
        variant="text"
        width={300}
        height={24}
        sx={{ mx: 'auto', bgcolor: 'rgba(255, 255, 255, 0.05)', mb: 2 }}
      />

      <Box className={styles.resultsGrid}>
        <Box
          className={`${styles.statCard} ${styles.largeCard}`}
          sx={{ gridColumn: '1 / -1', bgcolor: 'var(--bg-panel)' }}
        >
          <Box
            className={styles.mainContent}
            sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Skeleton
                variant="circular"
                width={64}
                height={64}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="text"
                  width={100}
                  height={20}
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', mb: 1 }}
                />
                <Skeleton
                  variant="text"
                  width={150}
                  height={32}
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                pl: { md: 4 },
                borderLeft: { md: '1px solid rgba(255, 255, 255, 0.1)' },
              }}
            >
              <Skeleton
                variant="text"
                width={180}
                height={20}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
              />
              <Skeleton
                variant="text"
                width={180}
                height={20}
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            gridColumn: '1 / -1',
            display: 'flex',
            gap: '20px',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {[1, 2, 3].map((i) => renderSkeletonCard(i))}
        </Box>

        <Box
          sx={{
            gridColumn: '1 / -1',
            display: 'flex',
            gap: '20px',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {[4, 5, 6, 7].map((i) => renderSkeletonCard(i))}
        </Box>

        <Box
          sx={{
            gridColumn: '1 / -1',
            display: 'flex',
            gap: '20px',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {[8, 9, 10, 11].map((i) => renderSkeletonCard(i))}
        </Box>

        <Box
          sx={{
            gridColumn: '1 / -1',
            display: 'flex',
            gap: '20px',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {[12, 13].map((i) => renderSkeletonCard(i))}
        </Box>
      </Box>

      <Box className={styles.resultsWrapper} sx={{ mt: 4 }}>
        <Skeleton
          variant="text"
          width={180}
          height={28}
          sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        />
        <Skeleton
          variant="rectangular"
          height={300}
          sx={{ borderRadius: 3, bgcolor: 'rgba(255, 255, 255, 0.03)' }}
        />
        <Skeleton
          variant="text"
          width={180}
          height={28}
          sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', mt: 4 }}
        />
        <Skeleton
          variant="rectangular"
          height={300}
          sx={{ borderRadius: 3, bgcolor: 'rgba(255, 255, 255, 0.03)' }}
        />
      </Box>
    </Box>
  );

  return (
    <Box className={styles.statisticsContainer}>
      {activeView === 'dashboard' ? (
        <>
          <Box className={styles.header}>
            <Box className={styles.filters}>
              <Box className={styles.filterItem}>
                <SiteSelector value={selectedSite} onChange={setSelectedSite} minWidth={150} />
              </Box>

              <Box className={styles.filterItem}>
                <RangeDatePicker
                  fromDate={fromDate}
                  toDate={toDate}
                  onFromChange={setFromDate}
                  onToChange={setToDate}
                />
              </Box>
            </Box>
          </Box>

          {sitesLoading || isLoadingStats ? (
            renderSkeletons()
          ) : stats ? (
            <>
              <Box className={styles.resultsWrapper}>
                <Typography className={styles.resultsHeader}>
                  Site statistics during the period {fromDate?.format('DD/MM/YYYY HH:mm')} -{' '}
                  {toDate?.format('DD/MM/YYYY HH:mm')}
                </Typography>

                <Box className={styles.resultsGrid}>
                  <StatCard
                    title="Site profit"
                    value={stats.siteProfit || '0'}
                    icon={BarChart3}
                    isLarge
                  >
                    <Box className={styles.extraContent}>
                      <Box className={styles.internalStat}>
                        <Typography className={styles.internalLabel}>FTD users:</Typography>
                        <Typography className={styles.internalValue}>
                          {stats.firstDepositUsers || 0}
                        </Typography>

                        <Button
                          size="small"
                          className={`${styles.cardActionBtn} ${styles.green}`}
                          sx={{ ml: 'auto', height: 24, fontSize: '10px !important' }}
                          onClick={() =>
                            fetchAndOpenList(
                              'FTD Users',
                              (page, limit) =>
                                statisticsService.getFirstDepositUsers({
                                  siteId: selectedSite,
                                  from: fromStr,
                                  to: toStr,
                                  page,
                                  limit,
                                }),
                              firstDepositUserColumns,
                              (items) =>
                                items.map((u: any, i: number) => ({
                                  id: u.id || i,
                                  userId: u.userId || u.id || i,
                                  username: u.username,
                                  email: u.email || '-',
                                  firstDepositAt: u.firstDepositAt,
                                })),
                            )
                          }
                        >
                          List
                        </Button>
                      </Box>
                      <Box className={styles.internalStat}>
                        <Typography className={styles.internalLabel}>New Registrants:</Typography>
                        <Typography className={styles.internalValue}>
                          {stats.newUsers || 0}
                        </Typography>
                        <Button
                          size="small"
                          className={`${styles.cardActionBtn} ${styles.green}`}
                          sx={{ ml: 'auto', height: 24, fontSize: '10px !important' }}
                          onClick={() =>
                            fetchAndOpenList(
                              'New Users',
                              (page, limit) =>
                                userService.getUsers({
                                  page,
                                  limit,
                                  siteId: selectedSite,
                                  from: fromStr,
                                  to: toStr,
                                  sortField: 'id',
                                  sortValue: 'DESC',
                                }),
                              newUsersColumns,
                              (items) =>
                                items.map((u: any, i: number) => ({
                                  id: u.id || i,
                                  userId: u.userId || u.id || i,
                                  username: u.username,
                                  email: u.email || '-',
                                  isEmailVerified: u.isEmailVerified,
                                })),
                            )
                          }
                        >
                          List
                        </Button>
                      </Box>
                      <Box className={styles.internalStat}>
                        <Typography className={styles.internalLabel}>Active Depositors:</Typography>
                        <Typography className={styles.internalValue}>
                          {stats.activeUsers || 0}
                        </Typography>
                        <Button
                          size="small"
                          className={`${styles.cardActionBtn} ${styles.green}`}
                          sx={{ ml: 'auto', height: 24, fontSize: '10px !important' }}
                          onClick={() =>
                            fetchAndOpenList(
                              'Active Users',
                              (page, limit) =>
                                statisticsService.getActiveUsers({
                                  siteId: selectedSite,
                                  from: fromStr,
                                  to: toStr,
                                  page,
                                  limit,
                                }),
                              defaultUserColumns,
                              (items) =>
                                items.map((u: any, i: number) => ({
                                  id: u.id || i,
                                  userId: u.userId || u.id || i,
                                  username: u.username,
                                  email: u.email || '-',
                                })),
                            )
                          }
                        >
                          List
                        </Button>
                      </Box>
                    </Box>
                  </StatCard>

                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      gap: '20px',
                      flexDirection: { xs: 'column', md: 'row' },
                    }}
                  >
                    <StatCard
                      title="Deposits"
                      value={stats.deposits?.total || '0'}
                      icon={ArrowUpCircle}
                      buttonConfig={{ label: 'List of users', color: 'orange' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Deposit Users',
                          (page, limit) =>
                            transactionService.getDepositTransactions({
                              page,
                              limit,
                              siteId: selectedSite,
                              dateFrom: fromStr,
                              dateTo: toStr,
                              // status: 'completed,pending,processing' as any,
                              sortField: 'id',
                              sortValue: 'DESC',
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((t: any, i: number) => ({
                              id: t.id || i,
                              userId: t.user?.id || t.userId || i,
                              username: t.user?.username || '-',
                              amount: t.amount,
                              date: t.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Withdrawals"
                      value={stats.withdrawals?.total || '0'}
                      icon={ArrowDownCircle}
                      subItems={[
                        { label: 'Completed', value: stats.withdrawals?.completed || '0' },
                        { label: 'In Process', value: stats.withdrawals?.inProcess || '0' },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'red' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Withdrawal Users',
                          (page, limit) =>
                            transactionService.getWithdrawTransactions({
                              page,
                              limit,
                              siteId: selectedSite,
                              dateFrom: fromStr,
                              dateTo: toStr,
                              // status: 'completed,pending,processing' as any,
                              sortField: 'id',
                              sortValue: 'DESC',
                            }),
                          withdrawUserColumns,
                          (items) =>
                            items.map((t: any, i: number) => ({
                              id: t.id || i,
                              userId: t.user?.id || t.userId || i,
                              username: t.user?.username || '-',
                              amount: t.amount,
                              date: t.createdAt,
                              status: t.status || '-',
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Commissions"
                      icon={RefreshCw}
                      subItems={[
                        { label: 'Finance Comm.', value: stats.financeCommission || '0' },
                        { label: 'Games Comm.', value: stats.gamesCommission || '0' },
                      ]}
                    />
                  </Box>

                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      gap: '20px',
                      flexDirection: { xs: 'column', md: 'row' },
                    }}
                  >
                    <StatCard
                      title="Promo Codes"
                      value={getBonusStats('promo_code').amount || '0'}
                      icon={Hash}
                      subItems={[
                        { label: 'Count', value: getBonusStats('promo_code').count || '0' },
                        { label: 'Activated', value: getBonusStats('promo_code').activated || '0' },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('promo_code').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'green' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Promo Code Users',
                          (page, limit) =>
                            promoCodeService.getPromoCodeUsages({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              page,
                              limit,
                              sortField: 'id',
                              sortValue: 'DESC',
                            }),
                          promoUserColumns,
                          (items) =>
                            items.map((p: any, i: number) => ({
                              id: p.id || i,
                              userId: p.user?.id || p.userId || i,
                              code: p.promoCode?.code || '-',
                              username: p.user?.username || '-',
                              amount: p.promoCode?.amount || '-',
                              date: p.usedAt || p.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Wheel Bonus"
                      value={getBonusStats('wheel_bonus').amount || '0'}
                      icon={Target}
                      subItems={[
                        { label: 'Count', value: getBonusStats('wheel_bonus').count || '0' },
                        {
                          label: 'Activated',
                          value: getBonusStats('wheel_bonus').activated || '0',
                        },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('wheel_bonus').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'magenta' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Wheel Bonus Users',
                          (page, limit) =>
                            statisticsService.getWheelSpins({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              page,
                              limit,
                            }),
                          wheelUserColumns,
                          (items) =>
                            items.map((w: any, i: number) => ({
                              id: w.id || i,
                              userId: w.user?.id || w.userId || i,
                              username: w.user?.username || '-',
                              levelKey: w.levelKey || w.level || '-',
                              prizeAmount: w?.prizeAmount || '-',
                              date: w.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Instant Rakeback"
                      value={getBonusStats('rakeback').amount || '0'}
                      icon={RotateCcw}
                      subItems={[
                        { label: 'Count', value: getBonusStats('rakeback').count || '0' },
                        { label: 'Activated', value: getBonusStats('rakeback').activated || '0' },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('rakeback').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'blue' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Rakeback Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'rakeback',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Monthly Bonus"
                      value={getBonusStats('monthly_bonus').amount || '0'}
                      icon={Calendar}
                      subItems={[
                        { label: 'Count', value: getBonusStats('monthly_bonus').count || '0' },
                        {
                          label: 'Activated',
                          value: getBonusStats('monthly_bonus').activated || '0',
                        },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('monthly_bonus').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'orange' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Monthly Bonus Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'monthly_bonus',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                  </Box>

                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      gap: '20px',
                      flexDirection: { xs: 'column', md: 'row' },
                    }}
                  >
                    <StatCard
                      title="Pre-Monthly Bonus"
                      value={getBonusStats('pre_monthly_bonus').amount || '0'}
                      icon={Gift}
                      subItems={[
                        { label: 'Count', value: getBonusStats('pre_monthly_bonus').count || '0' },
                        {
                          label: 'Activated',
                          value: getBonusStats('pre_monthly_bonus').activated || '0',
                        },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('pre_monthly_bonus').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'cyan' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Pre-Monthly Bonus Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'pre_monthly_bonus',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Weekly Boost"
                      value={getBonusStats('weekly_boost').amount || '0'}
                      icon={Zap}
                      subItems={[
                        { label: 'Count', value: getBonusStats('weekly_boost').count || '0' },
                        {
                          label: 'Activated',
                          value: getBonusStats('weekly_boost').activated || '0',
                        },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('weekly_boost').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'green' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Weekly Boost Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'weekly_boost',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Quarterly Bonus"
                      value={getBonusStats('quarterly_bonus').amount || '0'}
                      icon={PieChart}
                      subItems={[
                        { label: 'Count', value: getBonusStats('quarterly_bonus').count || '0' },
                        {
                          label: 'Activated',
                          value: getBonusStats('quarterly_bonus').activated || '0',
                        },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('quarterly_bonus').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'magenta' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Quarterly Bonus Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'quarterly_bonus',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="Free Spins"
                      value={getBonusStats('free_spin').amount || '0'}
                      icon={Coins}
                      subItems={[
                        { label: 'Count', value: getBonusStats('free_spin').count || '0' },
                        { label: 'Activated', value: getBonusStats('free_spin').activated || '0' },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('free_spin').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'blue' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Free Spins Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'free_spin',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                  </Box>

                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      gap: '20px',
                      flexDirection: { xs: 'column', md: 'row' },
                    }}
                  >
                    <StatCard
                      title="Lossback"
                      value={'0'}
                      icon={Undo}
                      subItems={[
                        { label: 'Count', value: getBonusStats('lossback').count || '0' },
                        { label: 'Activated', value: getBonusStats('lossback').activated || '0' },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('lossback').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'orange' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Free Spins Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'lossback',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                    <StatCard
                      title="VIP Bonus"
                      value={'0'}
                      icon={Crown}
                      subItems={[
                        { label: 'Count', value: getBonusStats('vip_bonus').count || '0' },
                        { label: 'Activated', value: getBonusStats('vip_bonus').activated || '0' },
                        {
                          label: 'Not Activated',
                          value: getBonusStats('vip_bonus').notActivated || '0',
                        },
                      ]}
                      buttonConfig={{ label: 'List of users', color: 'red' }}
                      onButtonClick={() =>
                        fetchAndOpenList(
                          'Free Spins Users',
                          (page, limit) =>
                            statisticsService.getBonusClaims({
                              siteId: selectedSite,
                              from: fromStr,
                              to: toStr,
                              bonusType: 'vip_bonus',
                              page,
                              limit,
                            }),
                          bonusUserColumns,
                          (items) =>
                            items.map((b: any, i: number) => ({
                              id: b.id || i,
                              userId: b.user?.id || b.userId || i,
                              username: b.user?.username || '-',
                              amount: b.amount || '-',
                              date: b.createdAt,
                            })),
                        )
                      }
                    />
                  </Box>
                </Box>
              </Box>
              <SystemsOverview
                theme={theme}
                siteId={selectedSite}
                fromDate={fromDate}
                toDate={toDate}
              />
            </>
          ) : null}
        </>
      ) : (
        <StatDataGridDetail
          onBack={() => setActiveView('dashboard')}
          title={modalTitle}
          columns={modalColumns}
          theme={theme}
          fetchFn={modalFetchFn}
        />
      )}
    </Box>
  );
};

export default Statistics;
